import React, { useContext, useEffect, useState } from "react";
import Layout from "../../components/Layout";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import _ from "lodash";
import { getMemberMetric, getMembers, verifyBreak } from "../../services";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { breaks, status as requestStatus } from "../../utils/Helpers";
import moment from "moment";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Search,
  Mail,
  Phone,
  ShieldCheck,
  MessageSquare,
  Clock,
  ExternalLink,
  Info,
  Circle,
  MoreHorizontal,
  Briefcase,
  CalendarDays,
  Activity,
  Plus
} from "lucide-react";

import { AppContext } from "../../utils/AppContext";
import Modals from "../../components/SharedComponets/Modals";
import MemberModal from "./AddMemberModal";

const Members = () => {
  const members = useSelector((state) => state.members.members);
  const [isProfilePopup, setIsProfilePopup] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loadingMemberId, setLoadingMemberId] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [memberToEdit, setMemberToEdit] = useState(null);
  const { setNewRequest, setAttendanceSocket, setBreakSocket } = useContext(AppContext);

  const notify = (message, type) => {
    toast[type](message, {
      position: "top-right",
      className: "dark:bg-gray-900 shadow-xl border border-gray-100 dark:border-gray-800",
    });
  };

  const fetchMetrics = async (memberId) => {
    try {
      const response = await getMemberMetric(memberId);
      setMetrics(response?.data?.Result);
    } catch (err) {
      console.error("Error fetching metrics:", err);
      notify("Failed to fetch metrics", "error");
    }
  };

  const fetchMembers = async () => {
    try {
      setIsLoading(true);
      await getMembers(0, 100);
      // members are synced via redux state
    } catch (err) {
      console.error("Error fetching members:", err);
      notify("Failed to fetch members", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [setNewRequest, setAttendanceSocket, setBreakSocket]);

  const handleEditMember = (e, member) => {
    e.stopPropagation();
    setMemberToEdit(member);
    setIsEditModalOpen(true);
  };

  const toggleProfile = (member = null) => {
    if (member) {
      fetchMetrics(member.Id);
    }
    setIsProfilePopup((prev) => !prev);
    setSelectedMember(member);
    document.body.classList.toggle("active-modal", !isProfilePopup);
  };

  const allReasons = Object.values(breaks).flatMap((breakType) =>
    breakType.reasons.map((reason) => ({
      id: reason.id,
      label: reason.label,
    }))
  );

  const getBreakReasonLabel = (breakReasonId) => {
    const foundReason = allReasons.find((reason) => reason.id === breakReasonId);
    return foundReason ? foundReason.label : "Away";
  };

  const getPerformanceDetails = (rate) => {
    const value = Number(rate);
    if (value >= 80) return { color: "#10b981", label: "Excellent" }; // Emerald-500
    if (value >= 50) return { color: "#f59e0b", label: "Good" };      // Amber-500
    return { color: "#f43f5e", label: "Needs Work" };                // Rose-500
  };

  const getCheckInDisplay = (member) => {
    const checkInTime =
      member?.CheckInTime ||
      member?.checkInTime ||
      member?.LastCheckInTime ||
      member?.TodayCheckInTime ||
      member?.Attendance?.CheckInTime ||
      member?.CurrentAttendance?.CheckInTime;

    if (!checkInTime) {
      return { text: "Absent", isCheckedIn: false };
    }

    const parsedTime = moment(checkInTime, [moment.ISO_8601, "HH:mm:ss"], true);
    if (!parsedTime.isValid()) {
      return { text: "Absent", isCheckedIn: false };
    }

    return { text: parsedTime.local().format("h:mm A"), isCheckedIn: true };
  };

  const filteredMembers = members.filter((member) => {
    const q = searchQuery.toLowerCase();
    const cnicRaw = (member.CNIC || member.cnic || "").toString().toLowerCase();
    const cnicNorm = cnicRaw.replace(/\s|-/g, "");
    const qNorm = q.replace(/\s|-/g, "");
    return (
      member.MemberName.toLowerCase().includes(q) ||
      (member.Designation && member.Designation.toLowerCase().includes(q)) ||
      (member.EmailAddress && member.EmailAddress.toLowerCase().includes(q)) ||
      (cnicRaw && (cnicRaw.includes(q) || (qNorm && cnicNorm.includes(qNorm))))
    );
  });

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50/50 dark:bg-[#060606] px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/10 rounded-xl">
                <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Team Directory</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Manage and track your organization's members</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="h-14 flex items-center gap-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl shadow-xl shadow-blue-600/20 transition-all active:scale-95 group"
              >
                <div className="p-1.5 bg-white/20 rounded-lg group-hover:scale-110 transition-transform">
                  <Plus className="w-5 h-5 text-white" />
                </div>
                <span className="uppercase tracking-widest text-sm">Add Member</span>
              </button>

              <div className="h-14 flex items-center gap-4 bg-white dark:bg-gray-900 px-6 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm min-w-[180px]">
                <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-xl">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex flex-col justify-center">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 font-black uppercase tracking-[0.15em] leading-none mb-1">Total Members</span>
                  <span className="text-xl font-black text-gray-900 dark:text-white leading-none tracking-tight">{members.length}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Members List Header (Desktop) */}
          <div className="hidden lg:grid grid-cols-[2fr_1.5fr_1.3fr_0.9fr_1fr_90px] gap-4 px-8 py-4 mb-4 text-xs font-semibold text-gray-400 dark:text-white uppercase tracking-[0.2em] border-b border-gray-100 dark:border-white/5">
            <div className="flex items-center gap-2 px-2">Member Info</div>
            <div className="flex items-center justify-center">Email Address</div>
            <div className="flex items-center justify-center">Phone</div>
            <div className="flex items-center justify-center">Check-In</div>
            <div className="flex items-center justify-center">Performance</div>
            <div className="text-right px-2">Actions</div>
          </div>

          {/* Members List Content */}
          <div className="space-y-4">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-24 bg-white dark:bg-gray-900 rounded-3xl animate-pulse" />
              ))
            ) : filteredMembers.length > 0 ? (
              <AnimatePresence mode="popLayout">
                {filteredMembers.map((member, index) => (
                  <motion.div
                    key={member.Id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => toggleProfile(member)}
                    className="group relative bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-[#111] rounded-[2rem] p-4 lg:px-8 border border-gray-100 dark:border-gray-800/50 shadow-sm hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 cursor-pointer lg:grid lg:grid-cols-[2fr_1.5fr_1.3fr_0.9fr_1fr_90px] items-center gap-4"
                  >
                    {/* Member Info */}
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="relative shrink-0">
                        <div className="w-14 h-14 rounded-2xl overflow-hidden border-2 border-gray-100 dark:border-gray-800 group-hover:border-blue-500 transition-colors bg-gray-100 dark:bg-gray-800">
                          <img
                            className="w-full h-full object-cover"
                            src={member.PhotoPath || "/default-avatar.png"}
                            alt={member.MemberName}
                          />
                        </div>
                        
                        
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">
                          {member.MemberName}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 font-medium">
                            <Briefcase className="w-3 h-3 text-blue-500" />
                            <span className="truncate max-w-[150px]">{member.Designation || "General"}</span>
                          </div>
                          <div className="lg:hidden flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 font-medium">
                            <Phone className="w-3 h-3 text-blue-500" />
                            <span>{member.PhoneNumber || "No Phone"}</span>
                          </div>
                          <div className="lg:hidden">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                                getCheckInDisplay(member).isCheckedIn
                                  ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"
                                  : "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400"
                              }`}
                            >
                              <Clock className="w-2.5 h-2.5" />
                              {getCheckInDisplay(member).text}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Email Address Column */}
                    <div className="hidden lg:flex items-center justify-center overflow-hidden">
                      <div className="flex items-center gap-2.5 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-300 rounded-2xl border border-gray-100 dark:border-gray-800/50 w-full max-w-full">
                        <Mail className="w-4 h-4 text-blue-500 shrink-0" />
                        <span className="text-[13px] font-bold truncate">
                          {member.EmailAddress || "No Email"}
                        </span>
                      </div>
                    </div>

                    {/* Phone Column */}
                    <div className="hidden lg:flex items-center justify-center overflow-hidden">
                      <div className="flex items-center gap-2.5 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-300 rounded-2xl border border-gray-100 dark:border-gray-800/50 w-full max-w-full">
                        <Phone className="w-4 h-4 text-blue-500 shrink-0" />
                        <span className="text-[13px] font-bold truncate">
                          {member.PhoneNumber || "No Phone"}
                        </span>
                      </div>
                    </div>

                    {/* Check-In Column */}
                    <div className="hidden lg:flex items-center justify-center">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                          getCheckInDisplay(member).isCheckedIn
                            ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"
                            : "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400"
                        }`}
                      >
                        <Clock className="w-3 h-3" />
                        {getCheckInDisplay(member).text}
                      </span>
                    </div>

                    {/* Performance Rate Column */}
                    <div className="hidden lg:flex flex-col items-center justify-center">
                      {member.PerformanceRate !== undefined && member.PerformanceRate !== null ? (
                        <div className="flex flex-col items-center gap-1">
                          <span
                            className="text-[13px] font-black tracking-tight"
                            style={{ color: getPerformanceDetails(member.PerformanceRate).color }}
                          >
                            {Number(member.PerformanceRate).toFixed(2)}%
                          </span>
                          <div className="w-16 h-1 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${member.PerformanceRate}%` }}
                              className="h-full"
                              style={{
                                backgroundColor: getPerformanceDetails(member.PerformanceRate).color,
                                boxShadow: `0 0 10px ${getPerformanceDetails(member.PerformanceRate).color}40`
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-700 font-bold uppercase text-[10px] tracking-widest">No Data</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="mt-4 lg:mt-0 flex items-center justify-end gap-2">
                      <button
                        onClick={(e) => handleEditMember(e, member)}
                        className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all shadow-sm"
                      >
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Space for future fields (Hidden indicator) */}
                    <div className="absolute top-0 right-0 p-2 opacity-0 group-hover:opacity-10 dark:text-white pointer-events-none">
                      <Info className="w-4 h-4" />
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            ) : (
              <div className="py-20 flex flex-col items-center justify-center bg-white dark:bg-gray-900 rounded-[3rem] border border-dashed border-gray-200 dark:border-gray-800 shadow-inner">
                <div className="p-6 bg-gray-50 dark:bg-[#0c0c0c] rounded-full mb-6 text-gray-400">
                  <Users className="w-12 h-12" />
                </div>
                <h3 className="text-xl font-bold dark:text-white">No Team Members Found</h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-sm text-center mt-2 font-medium">We couldn't find any members matching your search criteria. Try a different query.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {isProfilePopup && selectedMember && (
        <Modals
          Member={selectedMember}
          toggleProfile={toggleProfile}
          metrics={metrics}
          isAttendance={true}
        />
      )}

      <MemberModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onRefresh={fetchMembers}
      />

      <MemberModal
        isOpen={isEditModalOpen}
        isEdit={true}
        initialData={memberToEdit}
        onClose={() => setIsEditModalOpen(false)}
        onRefresh={fetchMembers}
      />
    </Layout>
  );
};

const Loader2 = ({ className }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

export default Members;